import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import axios from "axios";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

const options = [
  { label: "Delayed", value: "Red" },
  { label: "Slightly Late", value: "Orange" },
  { label: "On Time", value: "Green" },
];
const API_BASE = process.env.REACT_APP_API_URL;

function Tracker() {
  const { releaseName, index } = useParams();
  const [activities, setActivities] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");
  const [dialogAction, setDialogAction] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [openReasonDialog, setOpenReasonDialog] = useState(false);
  const [reasonStatus, setReasonStatus] = useState("");
  const [reasonComment, setReasonComment] = useState("");
  const [reasonError, setReasonError] = useState("");
  const [notificationPermission, setNotificationPermission] = useState(null);
  const [morechanges, setMoreChanges] = useState(false);
  const [openInfoDialog, setOpenInfoDialog] = useState(false);
  const [searchParams] = useSearchParams();
  const [allUsersData, setAllUsersData] = useState([]);

  const [value, setValue] = useState(null);
  const [comment, setComment] = useState("");
  const [otherInfo, setOtherInfo] = useState(null);

  const [applications, setApplications] = useState([]);
  const [environments, setEnvironments] = useState([]);

  const [appValue, setAppValue] = useState(null);
  const [envValue, setEnvValue] = useState(null);

  const fetchApplications = async () => {
    try {
      const areaIndex = index !== undefined ? index : 0;
      const response = await fetch(`${API_BASE}/areas/${areaIndex}/releases`);

      if (!response.ok) {
        throw new Error(`Failed to fetch releases: ${response.status}`);
      }

      const areaData = await response.json();
      const releases = areaData.releases || [];

      if (releases && releases.length > 0) {
        const appOptions = releases.map((app) => ({
          label: app.name,
          value: app.name,
        }));

        setApplications(appOptions);

        // Find the app that matches the release name from URL
        const matchingApp = appOptions.find((app) => app.value === releaseName);

        // If there's a matching app from URL, use it; otherwise use the first app
        const defaultApp = matchingApp || appOptions[0];
        setAppValue(defaultApp);

        // Get environments for the selected app
        const defaultAppData = releases.find(
          (app) => app.name === defaultApp.value
        );
        if (defaultAppData && defaultAppData.environments) {
          const envOptions = defaultAppData.environments.map((env) => ({
            label: env,
            value: env,
          }));

          setEnvironments(envOptions);

          const prodEnv = envOptions.find((env) => env.value === "PROD");
          if (prodEnv) {
            setEnvValue(prodEnv);
          } else if (envOptions.length > 0) {
            setEnvValue(envOptions[0]);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching releases:", error);
    }
  };

  const updateEnvironments = (appName) => {
    // Use the same area-based API for consistency
    const areaIndex = index !== undefined ? index : 0;
    fetch(`${API_BASE}/areas/${areaIndex}/releases`)
      .then((response) => response.json())
      .then((areaData) => {
        const releases = areaData.releases || [];
        const selectedApp = releases.find((app) => app.name === appName);

        if (selectedApp && selectedApp.environments) {
          const envOptions = selectedApp.environments.map((env) => ({
            label: env,
            value: env,
          }));

          setEnvironments(envOptions);

          if (envOptions.length > 0) {
            const prodEnv = envOptions.find((env) => env.value === "PROD");
            setEnvValue(prodEnv || envOptions[0]);
          } else {
            setEnvValue(null);
          }
        } else {
          setEnvironments([]);
          setEnvValue(null);
        }
      })
      .catch((error) => {
        console.error("Error fetching environments:", error);
        setEnvironments([]);
        setEnvValue(null);
      });
  };

  const fetchData = async () => {
    if (!appValue || !envValue) return;
    console.log("releaseName:", releaseName);
    try {
      const response = await axios.get(
        `${API_BASE}/Activities?AppName=${releaseName}`
      );
      console.log("---response--", response.data);
      setActivities(response.data.activities || []);
      setOtherInfo(response.data.header || {});
    } catch (error) {
      console.error("Error fetching Activities:", error);
    }
  };

  const updateActivities = async (dbData) => {
    try {
      const headers = {
        "Content-Type": "application/json",
      };
      await axios.post(
        `${API_BASE}/Activities?AppName=${releaseName}`,
        dbData,
        { headers: headers }
      );
      fetchData();
    } catch (error) {
      console.error("Error updating activities:", error);
    }
  };

  useEffect(() => {
    if (!("Notification" in window)) {
      console.log("Browser does not support desktop notification");
    } else {
      if (Notification.permission === "granted") {
        setNotificationPermission("granted");
      } else if (Notification.permission === "denied") {
        setNotificationPermission("denied");
      } else {
        Notification.requestPermission().then((permission) => {
          setNotificationPermission(permission);
        });
      }
    }
  }, []);

  useEffect(() => {
    fetchApplications();
    fetchAllAreas();
  }, []);

  function getUsersByReleaseId(areaData, releaseId) {
    for (const area of areaData) {
      const release = area.releases.find((r) => r.releaseId === releaseId);
      if (release) {
        return release.users;
      }
    }
    return [];
  }

  async function fetchAllAreas() {
    try {
      const res = await fetch(`${API_BASE}/areas`);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(
          errorData.error || `Failed to fetch: ${res.status} ${res.statusText}`
        );
      }
      const data = await res.json();
      const users = getUsersByReleaseId(data, releaseName);
      setAllUsersData(users);
    } catch (err) {
      console.error("Error fetching areas:", err);
    }
  }

  useEffect(() => {
    if (appValue && envValue) {
      fetchData();
    }
  }, [envValue, appValue]);

  const handleStartTime = (task) => {
    setDialogMessage(`Are you sure you want to start ${task.Title}?`);
    setDialogAction("start");
    setSelectedTask(task);
    setOpenDialog(true);
  };

  const handleEndTime = (task) => {
    setDialogMessage(`Are you sure you want to end ${task.Title}?`);
    setDialogAction("end");
    setSelectedTask(task);
    setOpenDialog(true);
  };

  const handleDialogConfirm = () => {
    setOpenDialog(false);

    if (!selectedTask || !dialogAction) {
      return;
    }

    const date = moment.tz("America/New_York");
    const timeString = date.format("h:mm A");
    const timeWithoutSeconds = timeString;

    const taskTitle = selectedTask.Title;
    let startTime, endTime;

    if (dialogAction === "start") {
      startTime = timeWithoutSeconds;
      if (notificationPermission === "granted") {
        new Notification(`Task ${selectedTask.Title} has started.`);
      }
    } else {
      endTime = timeWithoutSeconds;
      if (notificationPermission === "granted") {
        new Notification(`Task ${selectedTask.Title} has ended.`);
      }
    }

    const dbData = {
      taskTitle,
      startTime,
      endTime,
      statusComment: "",
    };
    console.log("---dbData-", dbData);
    updateActivities(dbData);
    setDialogAction(null);
    setSelectedTask(null);
  };

  const handleDialogCancel = () => {
    setOpenDialog(false);
    setDialogAction(null);
    setSelectedTask(null);
  };

  const handleStatusDialogOpen = (task, status) => {
    setSelectedTask(task);
    setReasonStatus(status);
    setReasonComment(task.Status === status ? task.StatusComment || "" : "");
    setReasonError("");
    setOpenReasonDialog(true);
  };

  const handleReasonDialogCancel = () => {
    setOpenReasonDialog(false);
    setReasonStatus("");
    setReasonComment("");
    setReasonError("");
  };

  const handleReasonDialogConfirm = () => {
    if (!selectedTask || !reasonStatus) {
      return;
    }

    if (!reasonComment.trim()) {
      setReasonError("Reason is required.");
      return;
    }

    const dbData = {
      taskTitle: selectedTask.Title,
      status: reasonStatus,
      statusComment: reasonComment.trim(),
    };

    updateActivities(dbData);
    handleReasonDialogCancel();
  };

  const handleInfoDialogOpen = () => {
    setOpenInfoDialog(true);
  };

  const handleInfoDialogCancel = () => {
    setOpenInfoDialog(false);
  };

  const handleInfoDialogConfirm = () => {
    setOpenInfoDialog(false);
    const dbData = {
      header: {
        color: value?.value,
        comment: comment ? comment : "",
      },
    };
    updateActivities(dbData);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Pending":
        return "#e74c3c";
      case "In Progress":
        return "#f1c40f";
      case "Completed":
        return "#2ecc71";
      case "Failed":
        return "#c62828";
      case "Blocked":
        return "#8e24aa";
      default:
        return "inherit";
    }
  };

  const getStatusCount = (activity, status) =>
    activity.Steps.reduce(
      (acc, step) =>
        acc + step.Tasks.filter((task) => task.Status === status).length,
      0
    );

  const getTaskReason = (task) => task.StatusComment || "";

  const totalTasks = activities.reduce(
    (acc, activity) =>
      acc + activity.Steps.reduce((acc, step) => acc + step.Tasks.length, 0),
    0
  );
  const completedTasks = activities.reduce(
    (acc, activity) =>
      acc +
      activity.Steps.reduce(
        (acc, step) =>
          acc + step.Tasks.filter((task) => task.Status === "Completed").length,
        0
      ),
    0
  );
  const overallCompletionPercentage = Math.round(
    (completedTasks / totalTasks) * 100
  );

  const gradientColor = (completionPercentage) => {
    if (completionPercentage <= 25) {
      return `linear-gradient(to right, #d1e1d8 ${completionPercentage}%, #fff ${completionPercentage}%)`;
    } else if (completionPercentage > 25 && completionPercentage <= 50) {
      return `linear-gradient(to right, #d1e1d8 25%, #98f7a9 ${completionPercentage}%, #fff ${completionPercentage}%)`;
    } else if (completionPercentage > 50 && completionPercentage <= 75) {
      return `linear-gradient(to right, #d1e1d8 25%, #98f7a9 50%, #61e17b  ${completionPercentage}%, #fff ${completionPercentage}%)`;
    } else {
      return `linear-gradient(to right, #d1e1d8 25%, #98f7a9 50%, #61e17b  75%, #0ee718 ${completionPercentage}%, #fff ${completionPercentage}%)`;
    }
  };

  function getTimeColor(task) {
    if (task["Start Time"] === "") {
      return;
    }
    const timeZone = "America/New_York";
    const plannedStartTime = moment.tz(
      `${task["Planned Date"]} ${task["Planned Start Time"]}`,
      "YYYY-MM-DD h:mm A",
      timeZone
    );
    const startTime = moment.tz(
      `${task["Planned Date"]} ${task["Start Time"]}`,
      "YYYY-MM-DD h:mm A",
      timeZone
    );
    const startDiff = moment
      .duration(startTime.diff(plannedStartTime))
      .asMinutes();
    const absStartDiff = Math.abs(startDiff);
    if (startTime.isSameOrBefore(plannedStartTime)) {
      return "green";
    }
    if (startDiff <= 0) {
      return "green";
    }
    if (absStartDiff > 0 && absStartDiff <= 30) {
      return "orange";
    }
    return "red";
  }

  function getEndTimeColor(task) {
    if (task["Start Time"] === "" || task["End Time"] === "") {
      return;
    }
    const timeZone = "America/New_York";
    const plannedEndTime = moment.tz(
      `${task["Planned Date"]} ${task["Planned End Time"]}`,
      "YYYY-MM-DD h:mm A",
      timeZone
    );
    const endTime = moment.tz(
      `${task["Planned Date"]} ${task["End Time"]}`,
      "YYYY-MM-DD h:mm A",
      timeZone
    );
    if (endTime.isBefore(plannedEndTime)) {
      return "green";
    }
    if (endTime.isSameOrBefore(plannedEndTime)) {
      return "green";
    }
    const thirtyMinutesLater = plannedEndTime.clone().add(30, "minutes");
    if (
      endTime.isAfter(plannedEndTime) &&
      endTime.isBefore(thirtyMinutesLater)
    ) {
      return "orange";
    }
    return "red";
  }

  function calculateTimeDifference(task) {
    const timeZone = "America/New_York";
    if (!task["Start Time"] && !task["End Time"]) {
      return;
    }
    if (task["Start Time"] && task["End Time"]) {
      const actualStartMoment = moment.tz(
        `${task["Start Time"]}`,
        "h:mm A",
        timeZone
      );
      const actualEndMoment = moment.tz(`${task["End Time"]}`, "h:mm A", timeZone);
      const timeDiff = actualEndMoment.diff(actualStartMoment, "minutes");
      return formatTimeDiff(timeDiff);
    } else if (task["Start Time"] && !task["End Time"]) {
      const actualStartMoment = moment.tz(
        `${task["Start Time"]}`,
        "h:mm A",
        timeZone
      );
      const actualEndMoment = moment.tz("America/New_York");
      const timeDiff = actualEndMoment.diff(actualStartMoment, "minutes");
      return formatTimeDiff(timeDiff);
    } else {
      return;
    }
  }

  function formatTimeDiff(timeDiff) {
    const hours = Math.floor(timeDiff / 60);
    const minutes = timeDiff % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
      2,
      "0"
    )}`;
  }

  function calculateTotalPlannedTime() {
    let totalPlannedTime = 0;
    const timeZone = "America/New_York";

    activities.forEach((activity) => {
      let smallestStartTime, largestEndTime;

      activity.Steps.forEach((step) => {
        step.Tasks.forEach((task) => {
          const plannedStartTime = moment.tz(
            `${task["Planned Date"]} ${task["Planned Start Time"]}`,
            "YYYY-MM-DD h:mm A",
            timeZone
          );
          const plannedEndTime = moment.tz(
            `${task["Planned Date"]} ${task["Planned End Time"]}`,
            "YYYY-MM-DD h:mm A",
            timeZone
          );

          if (
            !smallestStartTime ||
            plannedStartTime.isBefore(smallestStartTime)
          ) {
            smallestStartTime = plannedStartTime;
          }
          if (!largestEndTime || plannedEndTime.isAfter(largestEndTime)) {
            largestEndTime = plannedEndTime;
          }
        });
      });

      if (smallestStartTime && largestEndTime) {
        const timeDiff = largestEndTime.diff(smallestStartTime, "minutes");
        totalPlannedTime += timeDiff;
      }
    });

    return totalPlannedTime;
  }

  function calculateTotalElapsedTime() {
    let totalElapsedTime = 0;
    const timeZone = "America/New_York";

    activities.forEach((activity) => {
      let smallestStartTime, largestEndTime;

      activity.Steps.forEach((step) => {
        step.Tasks.forEach((task) => {
          let startTime, endTime;

          if (task["Start Time"] && task["End Time"]) {
            startTime = moment.tz(
              `${task["Planned Date"]} ${task["Start Time"]}`,
              "YYYY-MM-DD h:mm A",
              timeZone
            );
            endTime = moment.tz(
              `${task["Planned Date"]} ${task["End Time"]}`,
              "YYYY-MM-DD h:mm A",
              timeZone
            );
          } else if (task["Start Time"] && !task["End Time"]) {
            startTime = moment.tz(
              `${task["Planned Date"]} ${task["Start Time"]}`,
              "YYYY-MM-DD h:mm A",
              timeZone
            );
            endTime = moment.tz("America/New_York");
          } else {
            return;
          }

          if (!smallestStartTime || startTime.isBefore(smallestStartTime)) {
            smallestStartTime = startTime;
          }

          if (!largestEndTime || endTime.isAfter(largestEndTime)) {
            largestEndTime = endTime;
          }
        });
      });

      if (smallestStartTime && largestEndTime) {
        const timeDiff = largestEndTime.diff(smallestStartTime, "minutes");
        totalElapsedTime += timeDiff;
      }
    });

    return totalElapsedTime;
  }

  function calculateDelay(totalPlannedTime, totalElapsedTime) {
    totalPlannedTime = Math.round(totalPlannedTime);
    totalElapsedTime = Math.round(totalElapsedTime);
    if (totalElapsedTime > totalPlannedTime) {
      const delayMinutes = totalElapsedTime - totalPlannedTime;
      const hours = Math.floor(delayMinutes / 60);
      const minutes = delayMinutes % 60;
      return `Delayed by ${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}`;
    } else if (totalElapsedTime === totalPlannedTime) {
      return "On Time";
    } else {
      const remainingMinutes = totalPlannedTime - totalElapsedTime;
      const hours = Math.floor(remainingMinutes / 60);
      const minutes = remainingMinutes % 60;
      return `Remaining  ${String(hours).padStart(2, "0")}:${String(
        minutes
      ).padStart(2, "0")}`;
    }
  }

  const totalPlannedMinutes = calculateTotalPlannedTime();
  const totalElapsedMinutes = calculateTotalElapsedTime();
  const plannedTime = `${String(Math.floor(totalPlannedMinutes / 60)).padStart(
    2,
    "0"
  )}:${String(totalPlannedMinutes % 60).padStart(2, "0")}`;
  const elapsedTime = `${String(Math.floor(totalElapsedMinutes / 60)).padStart(
    2,
    "0"
  )}:${String(totalElapsedMinutes % 60).padStart(2, "0")}`;
  const delayStatus = calculateDelay(totalPlannedMinutes, totalElapsedMinutes);

  function getCookie(cname) {
    let name = cname + "=";
    let decodedCookie = decodeURIComponent(document.cookie);
    let ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) == " ") {
        c = c.substring(1);
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length);
      }
    }
    return "";
  }

  return (
    <Box className="releases-container">
      <Box className="releases-header">
        <Typography variant="h5" className="page-title">
          {searchParams.get("releaseName")}: {envValue?.value}
          <span style={{ margin: "5px" }}></span>
          <Tooltip title={otherInfo?.comment} placement="right" arrow>
            <InfoOutlinedIcon
              onClick={handleInfoDialogOpen}
              className="info-icon"
              sx={{ color: otherInfo?.color }}
            />
          </Tooltip>
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {morechanges && (
            <>
              <Typography variant="body1" className="tracker-stats">
                Deployment Duration: {plannedTime}
              </Typography>
              <Typography variant="body1" className="tracker-stats">
                Elapsed Duration: {elapsedTime}
              </Typography>
              <Typography variant="body1" className="tracker-stats">
                Duration: {delayStatus}
              </Typography>
            </>
          )}
          <Typography variant="body1" className="tracker-completion">
            Completion: {overallCompletionPercentage}%
          </Typography>

          {/* <Autocomplete
            options={applications}
            value={searchParams.get("releaseName")}
            onChange={(e, newValue) => {
              setAppValue(newValue);
              if (newValue) {
                updateEnvironments(newValue.value);
              } else {
                setEnvironments([]);
                setEnvValue(null);
              }
            }}
            clearIcon={null}
            className="autocomplete-field"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Application"
                className="autocomplete-input-right"
              />
            )}
          />
          <Autocomplete
            options={environments}
            value={envValue}
            onChange={(e, newValue) => setEnvValue(newValue)}
            clearIcon={null}
            className="autocomplete-field"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Environment"
                className="autocomplete-input"
              />
            )}
          /> */}
        </Box>
      </Box>

      {activities.map((activity) => {
        const completionPercentage = Math.round(
          (activity.Steps.reduce(
            (acc, step) =>
              acc +
              step.Tasks.filter((task) => task.Status === "Completed").length,
            0
          ) /
            activity.Steps.reduce((acc, step) => acc + step.Tasks.length, 0)) *
            100
        );
        return (
          <Accordion key={activity.Title} sx={{ marginBottom: 2 }}>
            <AccordionSummary
              expandIcon={
                <ExpandMoreIcon sx={{ transition: "all 0.3s ease-in-out" }} />
              }
              sx={{
                backgroundColor: gradientColor(completionPercentage),
                background: gradientColor(completionPercentage),
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                  }}
                >
                  {activity.Title} [
                  {activity.Steps.reduce(
                    (acc, step) => acc + step.Tasks.length,
                    0
                  )}{" "}
                  Task]
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ marginRight: "10px" }}>
                    {getStatusCount(activity, "Pending") > 0 && (
                      <span style={{ color: "red", marginRight: "10px" }}>
                        Pending:
                        <span style={{ fontWeight: "bold" }}>
                          {getStatusCount(activity, "Pending")}
                        </span>
                      </span>
                    )}
                    {getStatusCount(activity, "In Progress") > 0 && (
                      <span style={{ color: "orange", marginRight: "10px" }}>
                        In Progress:
                        <span style={{ fontWeight: "bold" }}>
                          {" "}
                          {getStatusCount(activity, "In Progress")}
                        </span>
                      </span>
                    )}
                    {getStatusCount(activity, "Completed") > 0 && (
                      <span style={{ color: "green", marginRight: "10px" }}>
                        Completed:
                        <span style={{ fontWeight: "bold" }}>
                          {getStatusCount(activity, "Completed")}
                        </span>
                      </span>
                    )}
                    {getStatusCount(activity, "Failed") > 0 && (
                      <span style={{ color: "#c62828", marginRight: "10px" }}>
                        Failed:
                        <span style={{ fontWeight: "bold" }}>
                          {getStatusCount(activity, "Failed")}
                        </span>
                      </span>
                    )}
                    {getStatusCount(activity, "Blocked") > 0 && (
                      <span style={{ color: "#8e24aa" }}>
                        Blocked:
                        <span style={{ fontWeight: "bold" }}>
                          {getStatusCount(activity, "Blocked")}
                        </span>
                      </span>
                    )}
                  </Typography>
                  <Typography>
                    Completion :{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {" "}
                      {completionPercentage}%{" "}
                    </span>
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails sx={{ transition: "all 0.3s ease-in-out" }}>
              {activity.Steps.map((step) => (
                <div key={step.Title}>
                  <Typography
                    variant="h6"
                    sx={{
                      marginBottom: 2,
                      transition: "all 0.3s ease-in-out",
                      textShadow: "0 0 5px rgba(0,0,0,0.1)",
                    }}
                  >
                    {step.Title}
                  </Typography>

                  <TableContainer
                    sx={{ marginBottom: 3, transition: "all 0.3s ease-in-out" }}
                  >
                    <Table
                      size="small"
                      aria-label="simple table"
                      sx={{ width: "100%", transition: "all 0.3s ease-in-out" }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "22%" }}>
                            Task Title
                          </TableCell>
                          <TableCell sx={{ width: "8%" }}>Team</TableCell>
                          <TableCell>Planned Date</TableCell>
                          <TableCell>Planned Start Time</TableCell>
                          <TableCell>Planned End Time</TableCell>
                          <TableCell>Start Time</TableCell>
                          <TableCell>End Time</TableCell>
                          <TableCell>Status</TableCell>
                          {morechanges && <TableCell>Duration</TableCell>}
                          {getCookie("user") && <TableCell>Action</TableCell>}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {step.Tasks.map((task) => (
                          <TableRow
                            key={task.Title}
                            sx={{ transition: "all 0.3s ease-in-out" }}
                          >
                            <TableCell>{task.Title}</TableCell>
                            <TableCell>{task.Team}</TableCell>
                            <TableCell> {task["Planned Date"]} </TableCell>
                            <TableCell>
                              {" "}
                              {task["Planned Start Time"]}{" "}
                            </TableCell>
                            <TableCell> {task["Planned End Time"]} </TableCell>
                            <TableCell sx={{ color: getTimeColor(task) }}>
                              {task["Start Time"]}
                            </TableCell>
                            <TableCell
                              sx={
                                morechanges
                                  ? { color: getEndTimeColor(task) }
                                  : {}
                              }
                            >
                              {task["End Time"]}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: getStatusColor(task.Status),
                                fontWeight: "medium",
                                transition: "all 0.3s ease-in-out",
                              }}
                            >
                              {(task.Status === "Failed" ||
                                task.Status === "Blocked") &&
                              getTaskReason(task) ? (
                                <Tooltip
                                  title={getTaskReason(task)}
                                  placement="top"
                                  arrow
                                >
                                  <span
                                    style={{
                                      borderBottom: "1px dashed currentColor",
                                      cursor: "help",
                                    }}
                                  >
                                    {task.Status}
                                  </span>
                                </Tooltip>
                              ) : (
                                task.Status
                              )}
                            </TableCell>

                            {morechanges && (
                              <TableCell>
                                {calculateTimeDifference(task)}{" "}
                              </TableCell>
                            )}
                            {getCookie("user") && (
                              <TableCell>
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    minWidth: "230px",
                                  }}
                                >
                                  {task["End Time"] === "" &&
                                    ["Pending", "Failed", "Blocked"].includes(
                                      task.Status
                                    ) && (
                                      <Button
                                        onClick={() => handleStartTime(task)}
                                        size="small"
                                        sx={{
                                          backgroundColor: "#e74c3c",
                                          color: "white",
                                          borderRadius: 2,
                                          padding: "5px 10px",
                                          transition: "all 0.3s ease-in-out",
                                        }}
                                      >
                                        Start
                                      </Button>
                                    )}
                                  {task.Status === "In Progress" &&
                                    task["Start Time"] !== "" &&
                                    task["End Time"] === "" &&
                                    (
                                      <Button
                                        onClick={() => handleEndTime(task)}
                                        size="small"
                                        sx={{
                                          backgroundColor: "#f1c40f",
                                          color: "white",
                                          borderRadius: 2,
                                          padding: "5px 10px",
                                          transition: "all 0.3s ease-in-out",
                                        }}
                                      >
                                        End
                                      </Button>
                                    )}
                                  {task["Start Time"] !== "" &&
                                    task["End Time"] !== "" && (
                                      <Button
                                        size="small"
                                        sx={{
                                          backgroundColor: "#4CAF50",
                                          color: "white",
                                          borderRadius: 2,
                                          padding: "5px 10px",
                                          pointerEvents: "none",
                                          transition: "all 0.3s ease-in-out",
                                        }}
                                      >
                                        Completed
                                      </Button>
                                    )}
                                  {task.Status !== "Completed" && (
                                    <>
                                      <Button
                                        onClick={() =>
                                          handleStatusDialogOpen(task, "Failed")
                                        }
                                        size="small"
                                        sx={{
                                          backgroundColor: "#c62828",
                                          color: "white",
                                          borderRadius: 2,
                                          padding: "5px 10px",
                                          transition: "all 0.3s ease-in-out",
                                        }}
                                      >
                                        Failed
                                      </Button>
                                      <Button
                                        onClick={() =>
                                          handleStatusDialogOpen(task, "Blocked")
                                        }
                                        size="small"
                                        sx={{
                                          backgroundColor: "#8e24aa",
                                          color: "white",
                                          borderRadius: 2,
                                          padding: "5px 10px",
                                          transition: "all 0.3s ease-in-out",
                                        }}
                                      >
                                        Blocked
                                      </Button>
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              ))}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <Dialog
        open={openDialog}
        onClose={handleDialogCancel}
        sx={{ transition: "all 0.3s ease-in-out" }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>Confirm Action</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "16px" }}>
            {dialogMessage}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "flex-end" }}>
          <Button
            onClick={handleDialogCancel}
            sx={{
              backgroundColor: "#e74c3c",
              color: "white",
              borderRadius: 2,
              padding: "5px 10px",
              transition: "all 0.3s ease-in-out",
              marginRight: 1,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDialogConfirm}
            sx={{
              backgroundColor: "#4CAF50",
              color: "white",
              borderRadius: 2,
              padding: "5px 10px",
              transition: "all 0.3s ease-in-out",
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openInfoDialog}
        onClose={handleInfoDialogCancel}
        className="dialog-container"
        PaperProps={{ className: "dialog-rounded" }}
      >
        <DialogTitle className="dialog-title">
          Status Update
          <Box className="dialog-title-line" />
        </DialogTitle>

        <DialogContent className="dialog-content">
          <Autocomplete
            options={options}
            value={value}
            onChange={(event, newValue) => {
              setValue(newValue);
            }}
            noOptionsText={<div>No options available.</div>}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Status"
                className="dialog-select-field"
              />
            )}
          />

          <TextField
            multiline
            rows={4}
            placeholder="Enter your comment here..."
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            label="Comment"
            fullWidth
            className="dialog-comment-field"
          />
        </DialogContent>

        <DialogActions className="dialog-actions">
          <Button
            onClick={handleInfoDialogCancel}
            variant="outlined"
            className="button-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleInfoDialogConfirm}
            variant="contained"
            className="button-confirm"
          >
            Update Status
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openReasonDialog}
        onClose={handleReasonDialogCancel}
        className="dialog-container"
        PaperProps={{ className: "dialog-rounded" }}
      >
        <DialogTitle sx={{ fontWeight: "bold" }}>
          Mark As {reasonStatus}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Provide the reason for marking this task as {reasonStatus}.
          </DialogContentText>
          <TextField
            autoFocus
            multiline
            minRows={3}
            fullWidth
            label="Reason"
            value={reasonComment}
            onChange={(event) => {
              setReasonComment(event.target.value);
              if (reasonError) {
                setReasonError("");
              }
            }}
            error={Boolean(reasonError)}
            helperText={reasonError || " "}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReasonDialogCancel}>Cancel</Button>
          <Button
            onClick={handleReasonDialogConfirm}
            variant="contained"
            sx={{ backgroundColor: reasonStatus === "Failed" ? "#c62828" : "#8e24aa" }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Tracker;
